import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { uploadAvatar, deleteAvatar } from '@/lib/employees';
import { employeesKeys } from '@/hooks/use-employees';
import { extractErrorMessage } from '@/lib/errors';
import type { EmployeeDetail } from '@/types/employee';
import { Loader2, AlertCircle, Upload, X, Trash2 } from 'lucide-react';

interface EditAvatarDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: EmployeeDetail;
}

function getInitials(firstName: string | null | undefined, lastName: string | null | undefined): string {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  return `${f}${l}`.toUpperCase() || '?';
}

export function EditAvatarDialog({ open, onOpenChange, employee }: EditAvatarDialogProps) {
  const queryClient = useQueryClient();
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isDeletingAvatar, setIsDeletingAvatar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const avatarUploadMutation = useMutation({
    mutationFn: (file: File) => uploadAvatar(employee.id, file),
    meta: { suppressGlobalToast: true },
    onSuccess: (avatarUrl) => {
      // Update local employee object to show new avatar immediately
      queryClient.setQueryData(['employee', employee.id], (old: EmployeeDetail | undefined) => {
        if (!old) return old;
        return { ...old, avatarUrl };
      });
      queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employee.id) });
      queryClient.invalidateQueries({ queryKey: employeesKeys.lists() });
      setAvatarFile(null);
      setAvatarPreview(null);
      setError(null);
      onOpenChange(false);
    },
  });

  const avatarDeleteMutation = useMutation({
    mutationFn: () => deleteAvatar(employee.id),
    meta: { suppressGlobalToast: true },
    onSuccess: async () => {
      // Update local employee object to remove avatar immediately
      queryClient.setQueryData(employeesKeys.detail(employee.id), (old: EmployeeDetail | undefined) => {
        if (!old) return old;
        return { ...old, avatarUrl: null };
      });

      // Invalidate queries to ensure fresh data from server
      await queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employee.id) });
      await queryClient.invalidateQueries({ queryKey: employeesKeys.lists() });

      setError(null);
      onOpenChange(false);
    },
  });

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPG, PNG, GIF, and WebP images are allowed.');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('File size exceeds 5MB limit.');
      return;
    }

    setAvatarFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;

    setIsUploadingAvatar(true);
    setError(null);
    try {
      await avatarUploadMutation.mutateAsync(avatarFile);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to upload avatar. Please try again.'));
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!employee.avatarUrl) return;

    setIsDeletingAvatar(true);
    setError(null);
    try {
      await avatarDeleteMutation.mutateAsync();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete avatar. Please try again.'));
    } finally {
      setIsDeletingAvatar(false);
    }
  };

  const handleClose = () => {
    if (!isUploadingAvatar && !isDeletingAvatar) {
      setAvatarFile(null);
      setAvatarPreview(null);
      setError(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Change Avatar</DialogTitle>
          <DialogDescription>
            Upload a new profile picture for {employee.firstName} {employee.lastName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Avatar Preview */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <Avatar className="h-32 w-32 border-4 border-background shadow-lg" key={employee.avatarUrl || 'no-avatar'}>
                {avatarPreview ? (
                  <AvatarImage src={avatarPreview} alt="Preview" />
                ) : employee.avatarUrl ? (
                  <AvatarImage src={employee.avatarUrl} alt={`${employee.firstName} ${employee.lastName}`} />
                ) : null}
                <AvatarFallback className="text-2xl font-semibold">
                  {getInitials(employee.firstName, employee.lastName)}
                </AvatarFallback>
              </Avatar>
              {avatarFile && (
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 cursor-pointer"
                  disabled={isUploadingAvatar}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* File Input */}
            <div className="w-full space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleAvatarSelect}
                className="hidden"
                id="avatar-upload"
                disabled={isUploadingAvatar}
              />
              <div className="flex gap-2 w-full">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar || isDeletingAvatar}
                  className="flex-1 cursor-pointer"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {avatarFile ? 'Change Photo' : 'Choose Photo'}
                </Button>
                {employee.avatarUrl && !avatarFile && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDeleteAvatar}
                    disabled={isUploadingAvatar || isDeletingAvatar}
                    className="cursor-pointer"
                  >
                    {isDeletingAvatar ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
              <p className="text-xs text-center text-muted-foreground">
                JPG, PNG, GIF or WebP. Max size 5MB.
              </p>
              {error && (
                <div className="flex items-center justify-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isUploadingAvatar || isDeletingAvatar}
          >
            Cancel
          </Button>
          {avatarFile && (
            <Button
              type="button"
              onClick={handleAvatarUpload}
              disabled={isUploadingAvatar || isDeletingAvatar}
            >
              {isUploadingAvatar && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Avatar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
